import { useRef, useState } from 'react'
import { PlaidAuthorization } from '../../utilities/CustomerData'
import { PlaidConfirmModal } from './PlaidModals/PlaidConfirmModal'
import { Autocomplete, useLoadScript } from '@react-google-maps/api'

export function PlaidAuthorizationModal({ _plaidAuthorization, paymentAmount, handleAgreement }) {
  const modalContentRef = useRef(null)

  const libraries = ['places']

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  })

  const [plaidAuthorization, setPlaidAuthorization] = useState(new PlaidAuthorization(_plaidAuthorization))
  const [showAddressInfo, setShowAddressInfo] = useState(new PlaidAuthorization(_plaidAuthorization).bankInfo.accountInfo.isValidAddress() ? false : true)
  const [clientSignature, setClientSignature] = useState('')
  const [currentDate, setCurrentDate] = useState(new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }))
  const [autocomplete, setAutocomplete] = useState(null)

  const [showPlaidConfirmModal, setShowPlaidConfirmModal] = useState(false)

  const handleLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance)
  }

  const handlePlaceChanged = () => {
    const getAddressComponent = (type, useShortName = false) => {
      const component = place.address_components.find(comp => comp.types.includes(type))
      return component ? (useShortName ? component.short_name : component.long_name) : ''
    }

    const place = autocomplete.getPlace()
    if (place) {
      setPlaidAuthorization((prevAuthorization) => ({
        ...prevAuthorization,
        bankInfo: {
          ...prevAuthorization.bankInfo,
          accountInfo: {
            ...prevAuthorization.bankInfo.accountInfo,
            accountAddressInfo: {
              ...prevAuthorization.bankInfo.accountInfo.accountAddressInfo,
              accountStreet: `${getAddressComponent('street_number')} ${getAddressComponent('route')}`.trim(),
              accountCity: getAddressComponent('locality'),
              accountState: getAddressComponent('administrative_area_level_1'),
              accountZip: getAddressComponent('postal_code')
            }
          }
        }
      }))
    }
  }

  const scrollToBottom = () => {
    if (modalContentRef.current)
      modalContentRef.current.scrollTo({ top: modalContentRef.current.scrollHeight, behavior: 'smooth' })
  }

  return (
    <div>
      {!showPlaidConfirmModal ? (
        <div className='fixed z-50 flex justify-center items-center top-0 left-0 w-full h-full bg-gray-900 bg-opacity-70'>
          <div className='relative flex flex-col lg:w-11/12 w-full lg:max-h-[90vh] h-full p-6 bg-white lg:rounded-lg shadow-xl'>
            <h2 className='text-2xl font-semibold text-gray-800 mb-4'>SETC Pros Inc. Pre-Authorization Debit Form</h2>
            <div ref={modalContentRef} className='w-full flex flex-col gap-2 overflow-y-auto'>
              <p><span className='font-bold'>Notice</span>: For your security, some institutions may have protected your account and routing numbers by tokenizing them. Rest assured, they are still securely linked to your account.</p>
              <img src={`data:image/png;base64,${plaidAuthorization.padBase64}`} />
              {showAddressInfo && (
                isLoaded && (
                  <div className='w-full flex flex-col items-start'>
                    <p className='font-bold mt-10 mb-4'>Address Information</p>
                    <div className='w-full relative mb-4'>
                      <p className='absolute text-sm bg-white left-3 top-[-5px]'>Street</p>
                      <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
                        <input
                          id='autocomplete'
                          type='text'
                          name='street'
                          placeholder='Street'
                          value={plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountStreet}
                          onChange={(e) => setPlaidAuthorization((prevAuthorization) => ({
                            ...prevAuthorization,
                            bankInfo: {
                              ...prevAuthorization.bankInfo,
                              accountInfo: {
                                ...prevAuthorization.bankInfo.accountInfo,
                                accountAddressInfo: {
                                  ...prevAuthorization.bankInfo.accountInfo.accountAddressInfo,
                                  accountStreet: e.target.value
                                }
                              }
                            }
                          }))}
                          className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                        />
                      </Autocomplete>
                    </div>
                    <div className='w-full relative mb-4'>
                      <p className='absolute text-sm bg-white left-3 top-[-5px]'>City</p>
                      <input
                        type='text'
                        name='city'
                        placeholder='City'
                        value={plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountCity}
                        onChange={(e) => setPlaidAuthorization((prevAuthorization) => ({
                          ...prevAuthorization,
                          bankInfo: {
                            ...prevAuthorization.bankInfo,
                            accountInfo: {
                              ...prevAuthorization.bankInfo.accountInfo,
                              accountAddressInfo: {
                                ...prevAuthorization.bankInfo.accountInfo.accountAddressInfo,
                                accountCity: e.target.value
                              }
                            }
                          }
                        }))}
                        className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                      />
                    </div>
                    <div className='w-full relative mb-4'>
                      <p className='absolute text-sm bg-white left-3 top-[-5px]'>State</p>
                      <input
                        type='text'
                        name='state'
                        placeholder='State'
                        value={plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountState}
                        onChange={(e) => setPlaidAuthorization((prevAuthorization) => ({
                          ...prevAuthorization,
                          bankInfo: {
                            ...prevAuthorization.bankInfo,
                            accountInfo: {
                              ...prevAuthorization.bankInfo.accountInfo,
                              accountAddressInfo: {
                                ...prevAuthorization.bankInfo.accountInfo.accountAddressInfo,
                                accountState: e.target.value
                              }
                            }
                          }
                        }))}
                        className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                      />
                    </div>
                    <div className='w-full relative mb-4'>
                      <p className='absolute text-sm bg-white left-3 top-[-5px]'>Zip</p>
                      <input
                        type='text'
                        name='zip'
                        placeholder='Zip'
                        value={plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountZip}
                        onChange={(e) => setPlaidAuthorization((prevAuthorization) => ({
                          ...prevAuthorization,
                          bankInfo: {
                            ...prevAuthorization.bankInfo,
                            accountInfo: {
                              ...prevAuthorization.bankInfo.accountInfo,
                              accountAddressInfo: {
                                ...prevAuthorization.bankInfo.accountInfo.accountAddressInfo,
                                accountZip: e.target.value
                              }
                            }
                          }
                        }))}
                        className='mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 focus:ring-indigo-300'
                      />
                    </div>
                  </div>
                )
              )}
              <div className='mb-4'>
                <label className='block mb-2 font-medium'>Client Signature</label>
                <input
                  type='text'
                  placeholder='Sign Your Name'
                  value={clientSignature}
                  onChange={(e) => setClientSignature(e.target.value)}
                  className='w-full p-2 border border-gray-300 rounded-md'
                  required
                />
                <label className='block mb-2 font-medium mt-4'>Client Date</label>
                <input
                  type='text'
                  placeholder='Enter Date'
                  value={currentDate}
                  readOnly
                  className='w-full p-2 border border-gray-300 rounded-md'
                />
              </div>
              <button
                onClick={() => setShowPlaidConfirmModal(true)}
                disabled={!clientSignature || !plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountStreet || !plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountCity || !plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountState || !plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountZip}
                className={`w-full p-2 rounded-md text-white ${!clientSignature || !plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountStreet || !plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountCity || !plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountState || !plaidAuthorization.bankInfo.accountInfo.accountAddressInfo.accountZip  ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}>
                Next
              </button>
            </div>
            <button
              onClick={scrollToBottom}
              className='w-fit ml-auto mt-4 p-2 bg-gray-800 rounded-full text-white shadow-lg hover:bg-gray-700'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='w-6 h-6'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
                strokeWidth='2'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M19 9l-7 7-7-7'
                />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <PlaidConfirmModal
          _plaidAuthorization={plaidAuthorization}
          paymentAmount={paymentAmount}
          handleAgreement={handleAgreement}
          clientSignature={clientSignature}
          currentDate={currentDate}
          setShowPlaidConfirmModal={setShowPlaidConfirmModal}
        />
      )}
    </div>
  )
}
